import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  apagarLead,
  ativarSite,
  conectarSite,
  configuracaoDoPlugin,
  contarLeads,
  listarLeads,
  listarSites,
  trocarChaveSite,
  type Lead,
  type SiteLeads,
} from '../api/leads'
import { KpiCard } from '../components/KpiCard'
import { useAuth } from '../state/AuthContext'
import { useFiltros } from '../state/FiltrosContext'
import { diasDoIntervalo, periodoAnterior } from '../utils/periodo'
import { caminhoDe, contarPor, csvDeLeads, formatarDataHora, linkWhatsApp, origemDoLead } from '../utils/leads'

const titulo = { fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }

function baixar(nome: string, conteudo: string) {
  const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/csv;charset=utf-8;' }))
  const a = document.createElement('a')
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * Leads dos formulários dos sites das marcas, no período e na marca do topo.
 * O site envia pelo plugin do WordPress (wordpress/marketinghub-leads), com a chave gerada em "Sites conectados".
 */
export function LeadsPage() {
  const { pode } = useAuth()
  const { intervalo, marcaId } = useFiltros()
  const [dados, setDados] = useState<{ leads: Lead[]; cortado: boolean; anterior: number } | null>(null)
  const [busca, setBusca] = useState('')
  const [aberto, setAberto] = useState<Lead | null>(null)
  const [sitesAbertos, setSitesAbertos] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const gerencia = pode('leads.gerenciar')
  const theme = useTheme()
  const celular = useMediaQuery(theme.breakpoints.down('sm'))

  const carregar = useCallback(async () => {
    try {
      const [atual, anterior] = await Promise.all([
        listarLeads(intervalo, marcaId || null),
        contarLeads(periodoAnterior(intervalo), marcaId || null),
      ])
      setDados({ ...atual, anterior })
    } catch (e) {
      setErro((e as Error).message)
    }
  }, [intervalo, marcaId])

  useEffect(() => {
    setDados(null)
    carregar()
  }, [carregar])

  const leads = useMemo(() => dados?.leads ?? [], [dados])
  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (!termo) return leads
    const digitos = termo.replace(/\D/g, '')
    return leads.filter(
      (l) =>
        [l.nome, l.email, l.mensagem].some((v) => v?.toLowerCase().includes(termo)) ||
        (digitos.length >= 4 && l.telefone?.replace(/\D/g, '').includes(digitos)),
    )
  }, [leads, busca])

  const porDia = useMemo(() => {
    const dias = diasDoIntervalo(intervalo)
    const conta = new Map(contarPor(leads, (l) => diaSP(l.enviadoEm)).map((x) => [x.chave, x.total]))
    return dias.map((d) => conta.get(d) ?? 0)
  }, [leads, intervalo])
  const porPagina = useMemo(() => contarPor(leads, (l) => l.paginaTitulo || caminhoDe(l.paginaUrl) || l.formulario || 'Sem página'), [leads])
  const porOrigem = useMemo(() => contarPor(leads, origemDoLead), [leads])

  const exportar = () => {
    const hoje = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' })
    baixar(`leads-${intervalo.inicio}-a-${intervalo.fim}-gerado-${hoje}.csv`, csvDeLeads(filtrados))
  }

  const total = leads.length
  const delta = dados && dados.anterior > 0 ? ((total - dados.anterior) / dados.anterior) * 100 : null

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="flex-start" spacing={2} useFlexGap flexWrap="wrap">
        <Box sx={{ mr: 'auto', maxWidth: 760 }}>
          <Typography variant="h5" sx={titulo}>
            Leads
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Quem preencheu os formulários dos sites das marcas, com a página e a origem do tráfego. Chegam na hora em
            que a pessoa envia — inclusive quem abre o WhatsApp e desiste de mandar a mensagem.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {gerencia ? (
            <Button variant="outlined" onClick={() => setSitesAbertos(true)} sx={{ textTransform: 'none' }}>
              Sites conectados
            </Button>
          ) : null}
          <Button variant="contained" disabled={!filtrados.length} onClick={exportar} sx={{ textTransform: 'none' }}>
            Exportar CSV
          </Button>
        </Stack>
      </Stack>

      {erro ? (
        <Alert severity="error" onClose={() => setErro(null)}>
          {erro}
        </Alert>
      ) : null}
      {dados?.cortado ? (
        <Alert severity="warning">Período com mais de 20 mil leads: a lista mostra só os 20 mil mais recentes. Escolha um período menor.</Alert>
      ) : null}

      {!dados ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' } }}>
            <KpiCard
              label="Leads no período"
              value={total.toLocaleString('pt-BR')}
              deltaPct={delta}
              deltaSufixo="vs período anterior"
              melhorQuando="sobe"
              sparkline={porDia}
              hint={dados.anterior === 0 && total > 0 ? 'Nenhum no período anterior' : undefined}
            />
            <Ranking titulo="Por página" itens={porPagina} />
            <Ranking titulo="Por origem" itens={porOrigem} />
          </Box>

          <TextField
            size="small"
            placeholder="Buscar por nome, e-mail, telefone ou mensagem"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            sx={{ maxWidth: 420 }}
          />

          {!filtrados.length ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography color="text.secondary">
                {busca ? 'Nenhum lead com essa busca no período.' : 'Nenhum lead no período.'}
                {!busca && gerencia ? ' Se o site ainda não envia, conecte-o em "Sites conectados".' : ''}
              </Typography>
            </Paper>
          ) : celular ? (
            <Stack spacing={1}>
              {filtrados.map((l) => (
                <Paper key={l.id} variant="outlined" onClick={() => setAberto(l)} sx={{ p: 1.5, cursor: 'pointer' }}>
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
                      {l.nome ?? '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {formatarDataHora(l.enviadoEm)}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" noWrap>
                    {[l.email, l.telefone].filter(Boolean).join(' · ')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {[l.paginaTitulo || caminhoDe(l.paginaUrl), origemDoLead(l), marcaId ? null : l.marca].filter(Boolean).join(' · ')}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Quando</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Contato</TableCell>
                    <TableCell>Página</TableCell>
                    <TableCell>Origem</TableCell>
                    {marcaId ? null : <TableCell>Marca</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtrados.map((l) => (
                    <TableRow key={l.id} hover onClick={() => setAberto(l)} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatarDataHora(l.enviadoEm)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{l.nome ?? '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{l.email ?? ''}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {l.telefone ?? ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" noWrap title={l.paginaUrl ?? undefined}>
                          {l.paginaTitulo || caminhoDe(l.paginaUrl) || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" noWrap>
                          {origemDoLead(l)}
                        </Typography>
                        {l.utmCampaign ? (
                          <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {l.utmCampaign}
                          </Typography>
                        ) : null}
                      </TableCell>
                      {marcaId ? null : <TableCell>{l.marca}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <DetalheDoLead
        lead={aberto}
        podeApagar={gerencia}
        onFechar={() => setAberto(null)}
        onApagado={() => {
          setAberto(null)
          carregar()
        }}
      />
      {gerencia ? <SitesConectados aberto={sitesAbertos} onFechar={() => setSitesAbertos(false)} /> : null}
    </Stack>
  )
}

const diaSP = (iso: string) => new Date(iso).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' })

function Ranking({ titulo: t, itens }: { titulo: string; itens: Array<{ chave: string; total: number }> }) {
  const mostrados = itens.slice(0, 5)
  const resto = itens.slice(5).reduce((s, x) => s + x.total, 0)
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
        {t}
      </Typography>
      {!itens.length ? (
        <Typography variant="body2" color="text.secondary">
          Sem leads no período.
        </Typography>
      ) : (
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          {mostrados.map((x) => (
            <Stack key={x.chave} direction="row" spacing={1} alignItems="baseline">
              <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }} title={x.chave}>
                {x.chave}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {x.total.toLocaleString('pt-BR')}
              </Typography>
            </Stack>
          ))}
          {resto ? (
            <Typography variant="caption" color="text.secondary">
              e mais {resto.toLocaleString('pt-BR')} em {itens.length - 5} outras
            </Typography>
          ) : null}
        </Stack>
      )}
    </Paper>
  )
}

function Campo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  if (children == null || children === '') return null
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '140px minmax(0, 1fr)' }, gap: { xs: 0, sm: 2 } }}>
      <Typography variant="body2" color="text.secondary">
        {rotulo}
      </Typography>
      <Typography variant="body2" component="div" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
        {children}
      </Typography>
    </Box>
  )
}

function DetalheDoLead(p: { lead: Lead | null; podeApagar: boolean; onFechar: () => void; onApagado: () => void }) {
  const [confirmando, setConfirmando] = useState(false)
  const [apagando, setApagando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const l = p.lead

  useEffect(() => {
    setConfirmando(false)
    setErro(null)
  }, [l])

  const apagar = async () => {
    if (!l) return
    setApagando(true)
    try {
      await apagarLead(l.id)
      p.onApagado()
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setApagando(false)
    }
  }

  const whats = linkWhatsApp(l?.telefone ?? null)
  // Os campos brutos que não viraram coluna (nomes internos do formulário).
  const extras = l ? Object.entries(l.campos).filter(([, v]) => v !== '' && v != null) : []

  return (
    <Dialog open={Boolean(l)} onClose={p.onFechar} fullWidth maxWidth="sm">
      {l ? (
        <>
          <DialogTitle sx={titulo}>{l.nome || l.email || l.telefone}</DialogTitle>
          <DialogContent>
            {erro ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {erro}
              </Alert>
            ) : null}
            <Stack spacing={1}>
              <Campo rotulo="Enviado em">{formatarDataHora(l.enviadoEm)}</Campo>
              <Campo rotulo="E-mail">{l.email ? <Link href={`mailto:${l.email}`}>{l.email}</Link> : null}</Campo>
              <Campo rotulo="Telefone">
                {l.telefone ? (
                  <>
                    {l.telefone}
                    {whats ? (
                      <>
                        {' · '}
                        <Link href={whats} target="_blank" rel="noopener">
                          abrir no WhatsApp
                        </Link>
                      </>
                    ) : null}
                  </>
                ) : null}
              </Campo>
              <Campo rotulo="Mensagem">{l.mensagem}</Campo>
              <Campo rotulo="Página">
                {l.paginaUrl ? (
                  <Link href={l.paginaUrl} target="_blank" rel="noopener">
                    {l.paginaTitulo || l.paginaUrl}
                  </Link>
                ) : null}
              </Campo>
              <Campo rotulo="Formulário">{l.formulario}</Campo>
              <Campo rotulo="Site">{`${l.fonte} · ${l.marca}`}</Campo>
              <Campo rotulo="Origem">{origemDoLead(l)}</Campo>
              <Campo rotulo="Campanha">{l.utmCampaign}</Campo>
              <Campo rotulo="Termo">{l.utmTerm}</Campo>
              <Campo rotulo="Conteúdo">{l.utmContent}</Campo>
              <Campo rotulo="gclid">{l.gclid}</Campo>
              <Campo rotulo="fbclid">{l.fbclid}</Campo>
              <Campo rotulo="Chegou de">{l.referrer}</Campo>
              {extras.length ? (
                <Box sx={{ pt: 1 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Campos do formulário
                  </Typography>
                  <Stack spacing={0.5}>
                    {extras.map(([k, v]) => (
                      <Campo key={k} rotulo={k}>
                        {typeof v === 'string' ? v : JSON.stringify(v)}
                      </Campo>
                    ))}
                  </Stack>
                </Box>
              ) : null}
              <Typography variant="caption" color="text.secondary" sx={{ pt: 1 }}>
                Recebido em {formatarDataHora(l.recebidoEm)}
                {l.ip ? ` · IP ${l.ip}` : ''}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            {p.podeApagar ? (
              confirmando ? (
                <>
                  <Typography variant="body2" color="error" sx={{ mr: 'auto', pl: 1 }}>
                    Apagar de vez? Não tem volta.
                  </Typography>
                  <Button onClick={() => setConfirmando(false)}>Não</Button>
                  <Button color="error" variant="contained" disabled={apagando} onClick={apagar}>
                    {apagando ? <CircularProgress size={20} color="inherit" /> : 'Apagar'}
                  </Button>
                </>
              ) : (
                <Button color="error" onClick={() => setConfirmando(true)} sx={{ mr: 'auto', textTransform: 'none' }}>
                  Apagar (pedido de exclusão)
                </Button>
              )
            ) : null}
            {!confirmando ? <Button onClick={p.onFechar}>Fechar</Button> : null}
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  )
}

/** Os sites que enviam leads, e a chave que o plugin do WordPress usa. */
function SitesConectados({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const { marcas } = useFiltros()
  const [sites, setSites] = useState<SiteLeads[] | null>(null)
  const [novo, setNovo] = useState<{ marcaId: string; nome: string } | null>(null)
  const [chave, setChave] = useState<{ site: string; chave: string } | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const nomeMarca = useMemo(() => new Map(marcas.map((m) => [m.id, m.nome])), [marcas])
  const plugin = configuracaoDoPlugin()

  const carregar = useCallback(async () => {
    try {
      setSites(await listarSites())
    } catch (e) {
      setErro((e as Error).message)
    }
  }, [])

  useEffect(() => {
    if (aberto) carregar()
    else {
      setChave(null)
      setNovo(null)
      setErro(null)
    }
  }, [aberto, carregar])

  const executar = async (acao: () => Promise<void>) => {
    setSalvando(true)
    setErro(null)
    try {
      await acao()
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      await carregar()
      setSalvando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="md">
      <DialogTitle sx={titulo}>Sites conectados</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cada site recebe uma chave. Instale o plugin <strong>MarketingHUB Leads</strong> no WordPress e cole nele o
          endereço, a chave pública e a chave do site. A marca do lead sai da chave — o site não escolhe.
        </Typography>
        {erro ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
            {erro}
          </Alert>
        ) : null}

        {chave ? (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setChave(null)}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Chave de <strong>{chave.site}</strong>. Copie agora: ela não aparece de novo (se perder, troque a chave).
            </Typography>
            <Stack spacing={1}>
              <Copiavel rotulo="Endereço do projeto" valor={plugin.url} />
              <Copiavel rotulo="Chave pública" valor={plugin.chavePublica} />
              <Copiavel rotulo="Chave do site" valor={chave.chave} />
            </Stack>
          </Alert>
        ) : null}

        {!sites ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : sites.length ? (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, opacity: salvando ? 0.6 : 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Site</TableCell>
                  <TableCell>Marca</TableCell>
                  <TableCell>Chave</TableCell>
                  <TableCell>Último lead</TableCell>
                  <TableCell>Ativo</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {sites.map((s) => (
                  <TableRow key={s.id} sx={{ opacity: s.ativa ? 1 : 0.6 }}>
                    <TableCell sx={{ fontWeight: 600 }}>{s.nome}</TableCell>
                    <TableCell>{nomeMarca.get(s.marcaId) ?? 'marca desativada'}</TableCell>
                    <TableCell>
                      <Box component="code">{s.chaveInicio}…</Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{s.ultimoLeadEm ? formatarDataHora(s.ultimoLeadEm) : 'Nenhum ainda'}</TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={s.ativa}
                        disabled={salvando}
                        onChange={(e) => executar(() => ativarSite(s.id, e.target.checked))}
                        slotProps={{ input: { 'aria-label': `Site ${s.nome} ativo` } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        disabled={salvando}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                        onClick={() =>
                          executar(async () => {
                            const r = await trocarChaveSite(s.id)
                            setChave({ site: s.nome, chave: r.chave })
                          })
                        }
                      >
                        Trocar chave
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Nenhum site conectado ainda.
          </Typography>
        )}

        {novo ? (
          <Stack
            component="form"
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'flex-start' }}
            onSubmit={(e) => {
              e.preventDefault()
              const { marcaId, nome } = novo
              executar(async () => {
                const r = await conectarSite(marcaId, nome.trim())
                setNovo(null)
                setChave({ site: nome.trim(), chave: r.chave })
              })
            }}
          >
            <TextField select size="small" label="Marca" value={novo.marcaId} onChange={(e) => setNovo({ ...novo, marcaId: e.target.value })} sx={{ minWidth: 180 }}>
              {marcas.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.nome}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Nome do site"
              placeholder="ex.: Site geoplas.com.br"
              value={novo.nome}
              onChange={(e) => setNovo({ ...novo, nome: e.target.value })}
              sx={{ flex: 1 }}
              autoFocus
            />
            <Button type="submit" variant="contained" disabled={salvando || !novo.marcaId || !novo.nome.trim()} sx={{ textTransform: 'none' }}>
              Conectar
            </Button>
            <Button onClick={() => setNovo(null)} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
          </Stack>
        ) : (
          <Button variant="outlined" onClick={() => setNovo({ marcaId: marcas[0]?.id ?? '', nome: '' })} sx={{ textTransform: 'none' }}>
            Conectar site
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}

function Copiavel({ rotulo, valor }: { rotulo: string; valor: string }) {
  const [copiado, setCopiado] = useState(false)
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ width: 130, flexShrink: 0 }}>
        {rotulo}
      </Typography>
      <Box component="code" sx={{ fontSize: 12, overflowWrap: 'anywhere', minWidth: 0, flex: 1 }}>
        {valor || '(não configurado neste app)'}
      </Box>
      <Button
        size="small"
        disabled={!valor}
        onClick={() =>
          navigator.clipboard.writeText(valor).then(() => {
            setCopiado(true)
            setTimeout(() => setCopiado(false), 2000)
          })
        }
        sx={{ textTransform: 'none', flexShrink: 0 }}
      >
        {copiado ? 'Copiado' : 'Copiar'}
      </Button>
    </Stack>
  )
}
